import { createStore } from 'zustand/vanilla';
import { devtools, persist } from 'zustand/middleware';

import {
  FundReturnKey,
  Product,
  ProductName,
  SortDirection,
  SortField,
  View,
} from '../product-center-table-wrapper/product-center-table-wrapper.model';
import { Constants } from '../product-center-table-wrapper/product-center-table-wrapper.constants';
import {
  FilterCategoryField,
  FilteredProducts,
  IFilter,
  IFilters,
  IGroupedFunds,
  IProductCenterStore,
  ISelectedProductFromGroup,
  IURLFilterList,
  TableTypeEnum,
  TileTabEnum,
} from './product-center.store.model';
import { ZustandStateToComponent } from './bridges/zustand-state-to-component';
import { FundsSearchService } from '../../common/fuse/fuseSearch';
import {
  filterProducts,
  getSelectedProductsField,
  hasAtLeastOneFilter,
  hasCategoryFieldAtLeastOneFilter,
  mapProductCodes,
  isProductChartEmpty,
  resetFilterCounts,
} from './product-center.store.utils';

export interface ITodoItem {
  value: string;
  checked?: boolean;
}
const fundSearchService: FundsSearchService = new FundsSearchService();

const initialState = {
  storeInitialized: false,
  originalProductCenterObject: { products: [], disclaimers: [] },
  filters: {
    [FilterCategoryField.PRODUCT_RANGE]: [],
    [FilterCategoryField.ASSET_CLASS]: [],
    [FilterCategoryField.GEOGRAPHICAL_EXPOSURE]: [],
    [FilterCategoryField.DISTRIBUTION_FREQUENCY]: [],
  },
  filteredProducts: [],
  filteredProductsShown: 0,
  performanceTileView: {
    sortField: SortField.FUND_NAME,
    sortDirection: SortDirection.ASC,
    selectedTab: Constants.TILES_TAB_INDEX.SINCE_INCEPTION,
    currentFundReturnKey: FundReturnKey.SINCE_INCEPTION,
    productsGroup: [],
    selectedProductFromGroup: [],
    selectedProducts: {
      [TileTabEnum.ONE_YEAR]: [],
      [TileTabEnum.THREE_YEARS]: [],
      [TileTabEnum.FIVE_YEARS]: [],
      [TileTabEnum.TEN_YEARS]: [],
      [TileTabEnum.SINCE_INCEPTION]: [],
    },
  },
  selectedView: View.TABLE,
  selectedTabInTableView: Constants.TABLE_TAB_INDEX.PERFORMANCE,
  searchTerm: '',
  selectedProducts: {
    [TableTypeEnum.PERFORMANCE]: [],
    [TableTypeEnum.PRICE]: [],
    [TableTypeEnum.ESG]: [],
    [TableTypeEnum.DOCUMENTS]: [],
    [TableTypeEnum.TILE_VIEW]: [],
  },
};

const productCenterStore = createStore<IProductCenterStore>()(
  devtools(
    (set, get) => {
      /**
       * Initial state.
       */
      return {
        ...initialState,
        /**
         * Action to toggle the selection of a product in a specific table.
         *
         * @param {string} selectedProductsField - The type of field where the product is being toggled.
         * @param {string} productCode - The code of the product to be toggled.
         * @param {boolean} shouldAdd - Indicates whether to add (true) or remove (false) the product.
         *
         * This action updates the `selectedProducts` state based on whether the product should be added or removed.
         * - Adds the product if `shouldAdd` is true and the product is not already selected.
         * - Removes the product if `shouldAdd` is false and the product is currently selected.
         */
        toggleCheckProduct: (selectedProductsField, productCode, shouldAdd) =>
          set(
            (state) => {
              const isTableView = state.selectedView === View.TABLE;
              const selectedProductsInView = isTableView
                ? state.selectedProducts
                : state.performanceTileView.selectedProducts;
              const existingProducts =
                selectedProductsInView[selectedProductsField] || [];

              let updatedProducts;

              if (shouldAdd) {
                if (!existingProducts.includes(productCode)) {
                  updatedProducts = [...existingProducts, productCode];
                }
              } else {
                updatedProducts = existingProducts.filter(
                  (product) => product !== productCode,
                );
              }

              if (
                updatedProducts &&
                updatedProducts.length !== existingProducts.length
              ) {
                if (isTableView) {
                  return {
                    ...state,
                    selectedProducts: {
                      ...state.selectedProducts,
                      [selectedProductsField]: updatedProducts,
                    },
                  };
                } else {
                  return {
                    ...state,
                    performanceTileView: {
                      ...state.performanceTileView,
                      selectedProducts: {
                        ...state.performanceTileView.selectedProducts,
                        [selectedProductsField]: updatedProducts,
                      },
                    },
                  };
                }
              }
              return state;
            },
            false,
            { type: 'toggleCheckProduct' },
          ),
        /**
         * Action to toggle the selection of all products in a specific table.
         *
         * @param {string} selectedProductsField - The type of field where the products are being toggled.
         *
         * This action updates the `selectedProducts` state based on whether all products should be selected or deselected.
         * - Deselects all products if all are currently selected.
         * - Selects all products if not all are currently selected.
         */
        toggleCheckAllProducts: (selectedProductsField) =>
          set(
            (state) => {
              const { performanceTileView, getFilteredProducts } = get();
              const isTableView = state.selectedView === View.TABLE;

              const filteredProducts = getFilteredProducts().map(
                (product: Product) => ({
                  ...product,
                  cumulativeChart: {
                    benchmarks: product.cumulativeChart?.benchmarks,
                    data: {
                      [performanceTileView.currentFundReturnKey]:
                        product.cumulativeChart?.data?.[
                          performanceTileView.currentFundReturnKey
                        ] ?? null,
                    },
                  },
                }),
              );

              const filteredProductsWithCumulativeData =
                filteredProducts.filter(
                  (product) =>
                    !isProductChartEmpty(product, performanceTileView),
                );

              const existingProducts = isTableView
                ? state.selectedProducts[selectedProductsField] || []
                : state.performanceTileView.selectedProducts[
                    selectedProductsField
                  ] || [];

              const allProductsSelected =
                existingProducts.length ===
                (isTableView
                  ? getFilteredProducts().length
                  : filteredProductsWithCumulativeData.length);

              const productsToSelect = isTableView
                ? getFilteredProducts()
                : filteredProductsWithCumulativeData;

              const updatedProducts = allProductsSelected
                ? []
                : productsToSelect.map((product) => product.productCode);

              return isTableView
                ? {
                    ...state,
                    selectedProducts: {
                      ...state.selectedProducts,
                      [selectedProductsField]: updatedProducts,
                    },
                  }
                : {
                    ...state,
                    performanceTileView: {
                      ...state.performanceTileView,
                      selectedProducts: {
                        ...state.performanceTileView.selectedProducts,
                        [selectedProductsField]: updatedProducts,
                      },
                    },
                  };
            },
            false,
            { type: 'toggleCheckAllProducts' },
          ),
        /**
         * Action to get the filtered list of products.
         *
         * This action retrieves the original product list and the filtered products from the state.
         * It creates a set of filtered product identifiers and returns the products from the original list
         * that match these identifiers.
         *
         * @returns {Array} - The filtered list of products.
         */
        getFilteredProducts: () => {
          const { originalProductCenterObject, filteredProducts } = get();
          const filteredSet = new Set(
            filteredProducts.map(
              (filteredProduct) =>
                `${filteredProduct.parentProductCode}-${filteredProduct.productCode}`,
            ),
          );

          return originalProductCenterObject.products.filter(
            (originalProduct) =>
              filteredSet.has(
                `${originalProduct.parentProductCode}-${originalProduct.productCode}`,
              ),
          );
        },
        /**
         * Action to get the selected product code from a specific product group.
         *
         * @param {string} parentProductCode - The parent product code to search for.
         *
         * This action retrieves the state and searches for a product in the `selectedProductFromGroup`
         * array that matches the given parent product code. It returns the product code of the matched
         * product or an empty string if no match is found.
         *
         * @returns {string} - The product code of the selected product or an empty string.
         */
        getSelectedProductFromGroup: (
          parentProductCode: Product['parentProductCode'],
        ) => {
          const state = get();
          const selectedProduct =
            state.performanceTileView.selectedProductFromGroup.find(
              (product) => product.parentProductCode === parentProductCode,
            );
          return selectedProduct?.productCode || '';
        },
        /**
         * Action to get the group of products for a specific parent product code.
         *
         * @param {string} parentProductCode - The parent product code to search for.
         *
         * This action retrieves the state and finds the group of products that match the given parent product code.
         * If no group is found, it returns an empty array. Otherwise, it maps the product codes in the group to their
         * corresponding original products, updating their cumulative chart data based on the current fund return key.
         *
         * @returns {Array<Product>} - The list of products in the group with updated cumulative chart data.
         */
        getProductsGroup: (parentProductCode: Product['parentProductCode']) => {
          const state = get();
          const groupedFund = state.performanceTileView.productsGroup.find(
            (group) => group.parentProductCode === parentProductCode,
          );

          if (!groupedFund) {
            return [];
          }

          const { currentFundReturnKey } = state.performanceTileView;
          const { products } = state.originalProductCenterObject;

          return groupedFund.products
            .map((productCode) => {
              const originalProduct = products.find(
                (product) => product.productCode === productCode,
              );

              if (originalProduct?.cumulativeChart?.data) {
                const newData =
                  originalProduct.cumulativeChart.data[currentFundReturnKey];
                const newCumulativeChartData = {
                  [currentFundReturnKey]: newData ?? [],
                };

                return {
                  ...originalProduct,
                  cumulativeChart: {
                    ...originalProduct.cumulativeChart,
                    data: newCumulativeChartData,
                  },
                };
              }

              return originalProduct;
            })
            .filter((product): product is Product => product !== undefined);
        },
        /**
         * Action to get the number of products shown based on the selected view.
         *
         * This action retrieves the selected view, the performance tile data and the filtered products from the state
         * and returns the number of products shown.
         * If the selected view is Table, it returns the total number of products.
         * If the selected view is Chart, it filters the products based on the current fund return key and returns the
         * number of products with non-empty cumulative chart data.
         *
         * @returns {number} - The total number of products shown based on the selected view.
         */
        getFilteredProductsShown: () => {
          const { selectedView, performanceTileView, getFilteredProducts } =
            get();

          const filteredProducts = getFilteredProducts().map(
            (product: Product): Product => {
              return {
                ...product,
                cumulativeChart: {
                  benchmarks: product.cumulativeChart?.benchmarks,
                  data: {
                    [performanceTileView.currentFundReturnKey]:
                      product.cumulativeChart?.data?.[
                        performanceTileView.currentFundReturnKey
                      ] ?? null,
                  },
                },
              };
            },
          );

          const isProductChartEmpty = (product: Product) => {
            return (
              product &&
              product.cumulativeChart &&
              product.cumulativeChart.data &&
              ((product.cumulativeChart.data[
                performanceTileView.currentFundReturnKey
              ] &&
                product.cumulativeChart?.data?.[
                  performanceTileView.currentFundReturnKey
                ]?.length === 0) ||
                !product.cumulativeChart.data[
                  performanceTileView.currentFundReturnKey
                ])
            );
          };

          if (selectedView === View.TABLE) {
            return getFilteredProducts().length;
          } else {
            return filteredProducts.filter(
              (product) => !isProductChartEmpty(product),
            ).length;
          }
        },
        /**
         * Action to get disclaimers by positioning.
         *
         * This action retrieves the original product center object from the state.
         * It filters the disclaimers based on the provided positioning and returns
         * the disclaimers that match the given positioning.
         *
         * @param {string} positioning - The positioning to filter disclaimers by.
         * @returns {Array} - The filtered list of disclaimers.
         */
        getDisclaimersByPositioning: (positioning) => {
          const { originalProductCenterObject } = get();

          if (!originalProductCenterObject.disclaimers) {
            return [];
          }

          return originalProductCenterObject.disclaimers.filter(
            (disclaimer) => disclaimer.positioning === positioning,
          );
        },
        /**
         * Action to get disclaimers by positioning.
         *
         * This action retrieves the original product center object from the state.
         * It filters the disclaimers based on the provided positioning and returns
         * the disclaimers that match the given positioning.
         *
         * @param {string} positioning - The positioning to filter disclaimers by.
         * @returns {Array} - The filtered list of disclaimers.
         */
        getDownloadProductsData: () => {
          const {
            selectedView,
            selectedTabInTableView,
            selectedProducts,
            getFilteredProducts,
            performanceTileView,
          } = get();

          const isTableView = selectedView === View.TABLE;
          const selectedTabInView = isTableView
            ? selectedTabInTableView
            : performanceTileView.selectedTab;
          const selectedProductsField = getSelectedProductsField(
            selectedView,
            selectedTabInView,
          );

          const existingProducts =
            (isTableView
              ? selectedProducts
              : performanceTileView.selectedProducts)[selectedProductsField] ||
            [];

          const filteredProducts = getFilteredProducts().map(
            (product: Product) => ({
              ...product,
              cumulativeChart: {
                benchmarks: product.cumulativeChart?.benchmarks,
                data: {
                  [performanceTileView.currentFundReturnKey]:
                    product.cumulativeChart?.data?.[
                      performanceTileView.currentFundReturnKey
                    ] ?? null,
                },
              },
            }),
          );

          const filteredProductsWithCumulativeData = filteredProducts.filter(
            (product) => !isProductChartEmpty(product, performanceTileView),
          );

          const relevantFilteredProducts = isTableView
            ? filteredProducts
            : filteredProductsWithCumulativeData;

          const filteredProductsInSelectionList = existingProducts.filter(
            (productCode) =>
              relevantFilteredProducts.some(
                (filteredProduct) =>
                  filteredProduct.productCode === productCode,
              ),
          );

          const totalProducts = filteredProductsInSelectionList.length;
          const isAllProductSelected =
            totalProducts === relevantFilteredProducts.length;

          return {
            totalProducts,
            isAllProductSelected,
            selectedProductsField,
          };
        },
        /**
         * Action to set the original product center object and initialize related state.
         *
         * @param {ProductCenterObject} productCenterObject - The product center object to set.
         *
         * This action updates the state with the provided product center object. It identifies duplicated
         * parent product codes, groups products by these codes, selects the first product from each group,
         * and creates a filtered products list. It then updates the state with these values and marks the store as initialized.
         *
         * @returns {void}
         */
        setOriginalProductCenterObject: (productCenterObject, classLabel) =>
          set(
            (state) => {
              if (!productCenterObject.products) {
                console.log('No products found in the product center object');
                return initialState;
              }

              const getDuplicatedParentProductCodes = (): string[] => {
                const codeCounts = productCenterObject.products.reduce(
                  (counts: { [key: string]: number }, item) => {
                    const code = item.parentProductCode ?? '';
                    counts[code] = (counts[code] || 0) + 1;
                    return counts;
                  },
                  {} as { [key: string]: number },
                );
                return Object.keys(codeCounts).filter(
                  (code) => codeCounts[code] > 1,
                );
              };

              const getProductsByParentCode = (
                parentProductCode: string,
              ): Product[] =>
                productCenterObject.products.filter(
                  (product) => product.parentProductCode === parentProductCode,
                );

              const parentCodesToMap = getDuplicatedParentProductCodes();

              const productsGroupMap: IGroupedFunds[] = parentCodesToMap.map(
                (parentProductCode) => {
                  const products = getProductsByParentCode(parentProductCode);
                  return {
                    parentProductCode,
                    products: products
                      .filter(
                        (product) =>
                          product &&
                          product.productCode !== null &&
                          product.productCode !== undefined,
                      )
                      .map((product) => product.productCode),
                  };
                },
              );

              const selectedProductFromGroup: ISelectedProductFromGroup[] =
                parentCodesToMap.map((parentProductCode) => {
                  const products = getProductsByParentCode(parentProductCode);
                  return {
                    parentProductCode,
                    productCode: products[0]?.productCode ?? '',
                  };
                });

              const filteredProducts: FilteredProducts[] = mapProductCodes(
                productCenterObject.products,
              );

              productCenterObject.products.forEach((product) => {
                if (product.productName && product.productName?.shareClass) {
                  product.productName.shareClassTerm = `${product.productName.shareClass} ${classLabel ?? Constants.CLASS}`;
                }
              });

              const filters: IFilters = {
                [FilterCategoryField.PRODUCT_RANGE]: [],
                [FilterCategoryField.ASSET_CLASS]: [],
                [FilterCategoryField.GEOGRAPHICAL_EXPOSURE]: [],
                [FilterCategoryField.DISTRIBUTION_FREQUENCY]: [],
              };

              productCenterObject.products.forEach((product) => {
                Object.values(FilterCategoryField).forEach((field) => {
                  const value = product.productName[field as keyof ProductName];
                  if (value) {
                    const filterCategory =
                      filters[field as FilterCategoryField];
                    if (filterCategory) {
                      const existingCategory = filterCategory.find(
                        (cat) => cat.name === value,
                      );
                      if (existingCategory) {
                        existingCategory.count += 1;
                      } else {
                        filterCategory.push({
                          name: value,
                          count: 1,
                          isSelected: false,
                        });
                      }
                    }
                  }
                });
              });

              return {
                ...state,
                originalProductCenterObject: { ...productCenterObject },
                filters,
                filteredProducts,
                performanceTileView: {
                  ...state.performanceTileView,
                  productsGroup: productsGroupMap,
                  selectedProductFromGroup,
                },
                storeInitialized: true,
              };
            },
            false,
            { type: 'setOriginalProductCenterObject' },
          ),
        /**
         * Action to set the filtered products and manage filters.
         *
         * @param {IFilter} [filterBy] - The filter criterion to add (optional).
         *
         * This action updates the state with the filtered products based on the provided filter criterion.
         * If no filter criterion is provided, it retains the existing filters in the state.
         * The function updates the filters by adding or removing the filter value for the specified category field.
         * It then filters the products based on the updated filters and sets the filtered products in the state.
         *
         * @returns {void}
         */
        setFilteredProducts: (filterBy?: IFilter) =>
          set(
            (state) => {
              fundSearchService.initFuse(
                state.originalProductCenterObject.products,
              );
              const updatedFilters = { ...state.filters };
              const productsToFilter: Product[] = state.searchTerm
                ? fundSearchService
                    .searchFunds(state.searchTerm)
                    .map((element) => element.item)
                : [...state.originalProductCenterObject.products];

              if (filterBy) {
                const { categoryField, filterValue } = filterBy;
                const filterCategory = updatedFilters[categoryField] || [];
                const existingCategory = filterCategory.find(
                  (cat) => cat.name === filterValue,
                );

                if (existingCategory) {
                  existingCategory.isSelected = !existingCategory.isSelected;
                } else {
                  filterCategory.push({
                    name: filterValue,
                    count: 1,
                    isSelected: true,
                  });
                }

                updatedFilters[categoryField] = filterCategory;
              }

              const updateFilterCounts = (
                products: Product[],
                excludeCurrentFilterCount: boolean,
              ) => {
                products.forEach((product) => {
                  Object.values(FilterCategoryField).forEach((field) => {
                    if (
                      !excludeCurrentFilterCount ||
                      filterBy?.categoryField !== field
                    ) {
                      const value =
                        product.productName[field as keyof ProductName];
                      if (value) {
                        const filterCategory =
                          updatedFilters[field as FilterCategoryField];
                        if (filterCategory) {
                          const existingCategory = filterCategory.find(
                            (cat) => cat.name === value,
                          );
                          if (existingCategory) {
                            existingCategory.count += 1;
                          }
                        }
                      }
                    }
                  });
                });
              };

              if (
                hasCategoryFieldAtLeastOneFilter(
                  updatedFilters,
                  filterBy?.categoryField,
                )
              ) {
                resetFilterCounts(
                  Object.values(FilterCategoryField).filter(
                    (field) => filterBy?.categoryField !== field,
                  ),
                  updatedFilters,
                );
                updateFilterCounts(
                  filterProducts(productsToFilter, updatedFilters),
                  true,
                );
              } else if (hasAtLeastOneFilter(updatedFilters)) {
                resetFilterCounts(
                  Object.values(FilterCategoryField),
                  updatedFilters,
                );
                updateFilterCounts(
                  filterProducts(productsToFilter, updatedFilters),
                  false,
                );
              } else {
                resetFilterCounts(
                  Object.values(FilterCategoryField),
                  updatedFilters,
                );
                updateFilterCounts(productsToFilter, false);
              }

              let newFilteredProducts = mapProductCodes(
                filterProducts(productsToFilter, updatedFilters),
              );

              if (
                newFilteredProducts.length === 0 &&
                !hasAtLeastOneFilter(updatedFilters)
              ) {
                newFilteredProducts = mapProductCodes(productsToFilter);
              }

              return {
                ...state,
                filteredProducts: newFilteredProducts,
                filters: updatedFilters,
              };
            },
            false,
            { type: 'setFilteredProducts' },
          ),
        /**
         * Action to set the filtered products and manage filters.
         *
         * @param {URLSearchParams} [urlParams] - The parameters that the url contains.
         *
         * This action updates the state with the filtered products based on the provided filter criterion.
         * The function updates the filters by adding or removing the filter value for the specified category field.
         * It then filters the products based on the updated filters and sets the filtered products in the state.
         *
         * @returns {void}
         */
        setFilteredProductsFromURLParams: (urlParams: URLSearchParams): void =>
          set(
            (state) => {
              // Reset filter list (unckeck all existing filters), and update the filteredProducts list
              get().resetFilters();

              const updatedFilters = { ...state.filters };
              const productsToFilter: Product[] = state.searchTerm
                ? fundSearchService
                    .searchFunds(state.searchTerm)
                    .map((element) => element.item)
                : [...state.originalProductCenterObject.products];

              // create a list of filters from the urlParams
              const urlFiltersList: IURLFilterList = {
                [FilterCategoryField.PRODUCT_RANGE]: [],
                [FilterCategoryField.ASSET_CLASS]: [],
                [FilterCategoryField.GEOGRAPHICAL_EXPOSURE]: [],
                [FilterCategoryField.DISTRIBUTION_FREQUENCY]: [],
              };
              urlParams.forEach((value, key) => {
                if (key in urlFiltersList) {
                  urlFiltersList[key].push(value);
                }
              });

              // Update the filters with the urlFiltersList
              Object.entries(urlFiltersList).forEach(
                ([categoryField, filterValues]) => {
                  const filterCategory =
                    updatedFilters[categoryField as FilterCategoryField] || [];
                  filterValues.forEach((filterValue) => {
                    const existingCategory = filterCategory.find(
                      (cat) => cat.name === filterValue,
                    );

                    if (existingCategory) {
                      existingCategory.isSelected = true;
                    } else {
                      filterCategory.push({
                        name: filterValue,
                        count: 1,
                        isSelected: true,
                      });
                    }
                  });
                  updatedFilters[categoryField as FilterCategoryField] =
                    filterCategory;
                },
              );

              // helper function to update the filter counts
              const updateFilterCounts = (
                products: Product[],
                excludeCurrentFilterCount: boolean,
              ) => {
                products.forEach((product) => {
                  Object.values(FilterCategoryField).forEach((field) => {
                    if (
                      !excludeCurrentFilterCount ||
                      urlFiltersList[field] !== undefined
                    ) {
                      const value =
                        product.productName[field as keyof ProductName];
                      if (value) {
                        const filterCategory =
                          updatedFilters[field as FilterCategoryField];
                        if (filterCategory) {
                          const existingCategory = filterCategory.find(
                            (cat) => cat.name === value,
                          );
                          if (existingCategory) {
                            existingCategory.count += 1;
                          }
                        }
                      }
                    }
                  });
                });
              };

              // Recaulculate filter counts
              resetFilterCounts(
                Object.values(FilterCategoryField),
                updatedFilters,
              );
              updateFilterCounts(productsToFilter, false);

              let newFilteredProducts = mapProductCodes(
                filterProducts(productsToFilter, updatedFilters),
              );

              if (
                newFilteredProducts.length === 0 &&
                !hasAtLeastOneFilter(updatedFilters)
              ) {
                newFilteredProducts = mapProductCodes(productsToFilter);
              }

              return {
                ...state,
                filteredProducts: newFilteredProducts,
                filters: updatedFilters,
              };
            },
            false,
            { type: 'setFilteredProductsFromURLParams' },
          ),
        /**
         * Action to reset the filters and update the filtered products.
         *
         * This action initializes the search service with the original products and resets the filters.
         * If a search term is present in the state, it performs a search and filters the products accordingly.
         * The filtered products are then updated in the state along with resetting the filter criteria.
         * If a categoryField is provided, it resets the filters for that specific category and updates the filtered products accordingly.
         *
         * @param {string} [categoryField] - The category field to reset in the filters.
         * @returns {void}
         */
        resetFilters: (categoryField) =>
          set(
            (state) => {
              fundSearchService.initFuse(
                state.originalProductCenterObject.products,
              );

              const productsToFilter: Product[] = state.searchTerm
                ? fundSearchService
                    .searchFunds(state.searchTerm)
                    .map((element) => element.item)
                : [...state.originalProductCenterObject.products];

              let newFilteredProducts: FilteredProducts[] =
                mapProductCodes(productsToFilter);

              const updateFilterCounts = (
                products: Product[],
                excludeCurrentFilterCount: boolean,
                updatedFilters: IFilters,
              ) => {
                products.forEach((product) => {
                  Object.values(FilterCategoryField).forEach((field) => {
                    if (!excludeCurrentFilterCount || categoryField !== field) {
                      const value =
                        product.productName[field as keyof ProductName];
                      if (value) {
                        const filterCategory =
                          updatedFilters[field as FilterCategoryField];
                        if (filterCategory) {
                          const existingCategory = filterCategory.find(
                            (cat) => cat.name === value,
                          );
                          if (existingCategory) {
                            existingCategory.count += 1;
                          }
                        }
                      }
                    }
                  });
                });
              };

              if (categoryField) {
                const updatedFilters = { ...state.filters };

                if (hasAtLeastOneFilter(updatedFilters)) {
                  if (updatedFilters[categoryField]) {
                    updatedFilters[categoryField] =
                      updatedFilters[categoryField]?.map((cat) => ({
                        ...cat,
                        isSelected: false,
                      })) || [];
                  }

                  if (hasAtLeastOneFilter(updatedFilters)) {
                    Object.values(FilterCategoryField).forEach((field) => {
                      const filterCategory = updatedFilters[field];
                      if (filterCategory) {
                        filterCategory.forEach((cat) => {
                          cat.count = 0;
                        });
                      }
                    });

                    updateFilterCounts(
                      filterProducts(productsToFilter, updatedFilters),
                      false,
                      updatedFilters,
                    );
                  } else {
                    Object.values(FilterCategoryField).forEach((field) => {
                      updatedFilters[field] =
                        updatedFilters[field]?.map((cat) => ({
                          ...cat,
                          count: 0,
                        })) || [];
                    });

                    updateFilterCounts(productsToFilter, false, updatedFilters);
                  }

                  newFilteredProducts = mapProductCodes(
                    filterProducts(productsToFilter, updatedFilters),
                  );
                }

                return {
                  ...state,
                  filteredProducts: newFilteredProducts,
                  filters: updatedFilters,
                };
              } else {
                const filters: IFilters = {
                  [FilterCategoryField.PRODUCT_RANGE]: [],
                  [FilterCategoryField.ASSET_CLASS]: [],
                  [FilterCategoryField.GEOGRAPHICAL_EXPOSURE]: [],
                  [FilterCategoryField.DISTRIBUTION_FREQUENCY]: [],
                };

                state.originalProductCenterObject.products.forEach(
                  (product) => {
                    Object.values(FilterCategoryField).forEach((field) => {
                      const value =
                        product.productName[field as keyof ProductName];
                      if (value) {
                        const filterCategory =
                          filters[field as FilterCategoryField];
                        if (filterCategory) {
                          const existingCategory = filterCategory.find(
                            (cat) => cat.name === value,
                          );
                          if (existingCategory) {
                            existingCategory.count += 1;
                          } else {
                            filterCategory.push({
                              name: value,
                              count: 1,
                              isSelected: false,
                            });
                          }
                        }
                      }
                    });
                  },
                );

                return {
                  ...state,
                  filteredProducts: newFilteredProducts,
                  filters,
                };
              }
            },
            false,
            { type: 'resetFilters' },
          ),
        /**
         * Action to set the selected view.
         *
         * @param {string} selectedView - The view to set as selected.
         *
         * This action updates the state with the provided selected view.
         *
         * @returns {void}
         */
        setSelectedView: (selectedView) =>
          set(
            (state) => ({
              ...state,
              selectedView,
            }),
            false,
            { type: 'setSelectedView' },
          ),
        /**
         * Sets the sort field and sort direction for the tile view in the performanceTileView state.
         *
         * @param {string} sortField - The new sort field to be set.
         * @param {string} sortDirection - The new sort direction to be set.
         */
        setSortInTileView: (sortField, sortDirection) =>
          set(
            (state) => ({
              ...state,
              performanceTileView: {
                ...state.performanceTileView,
                sortField: sortField ?? state.performanceTileView.sortField,
                sortDirection:
                  sortDirection ?? state.performanceTileView.sortDirection,
              },
            }),
            false,
            { type: 'setSortInTileView' },
          ),
        /**
         * Action to set the search term.
         *
         * @param {string} searchTerm - The search term to set.
         *
         * This action updates the state with the provided search term.
         *
         * @returns {void}
         */
        setSearchTerm: (searchTerm, customSearch) =>
          set(
            (state) => {
              fundSearchService.initFuse(
                state.originalProductCenterObject.products,
              );

              let productsToFilter: Product[] = [];
              let newFilteredProducts: FilteredProducts[] = [];

              if (customSearch?.searchAllProducts) {
                productsToFilter = state.originalProductCenterObject.products;
              } else {
                const fuseResults = fundSearchService.searchFunds(
                  searchTerm ?? '',
                );
                productsToFilter = fuseResults.map((element) => element.item);
              }

              const updatedFilters = { ...state.filters };
              const updateFilterCounts = (
                products: Product[],
                excludeCurrentFilterCount: boolean,
                filters: IFilters,
              ) => {
                Object.values(FilterCategoryField).forEach((field) => {
                  filters[field] =
                    filters[field]?.map((cat) => ({
                      ...cat,
                      count: 0,
                    })) || [];
                });

                products.forEach((product) => {
                  Object.values(FilterCategoryField).forEach((field) => {
                    if (!excludeCurrentFilterCount) {
                      const value =
                        product.productName[field as keyof ProductName];
                      if (value) {
                        const filterCategory =
                          filters[field as FilterCategoryField];
                        if (filterCategory) {
                          const existingCategory = filterCategory.find(
                            (cat) => cat.name === value,
                          );
                          if (existingCategory) {
                            existingCategory.count += 1;
                          }
                        }
                      }
                    }
                  });
                });
              };

              if (hasAtLeastOneFilter(state.filters)) {
                updateFilterCounts(
                  filterProducts(productsToFilter, updatedFilters),
                  false,
                  updatedFilters,
                );

                newFilteredProducts = mapProductCodes(
                  filterProducts(productsToFilter, updatedFilters),
                );
              } else {
                updateFilterCounts(productsToFilter, false, updatedFilters);

                newFilteredProducts = mapProductCodes(productsToFilter);
              }

              return {
                ...state,
                filters: updatedFilters,
                filteredProducts: newFilteredProducts,
                searchTerm: searchTerm,
              };
            },
            false,
            { type: 'setSearchTerm' },
          ),
        /**
         * Action to set the selected product from a group based on the parent product code.
         *
         * @param {string} parentProductCode - The parent product code to match.
         * @param {string} productCode - The product code to set as selected.
         *
         * This action updates the state by setting the selected product for the given parent product code.
         * If the parent product code already exists in the selected products group, it updates the product code.
         * If it does not exist, it adds a new entry with the parent product code and product code.
         *
         * @returns {void}
         */
        setSelectedProductFromGroup: (parentProductCode, productCode) =>
          set(
            (state) => {
              const selectedProductFromGroup =
                state.performanceTileView.selectedProductFromGroup.map(
                  (item) =>
                    item.parentProductCode === parentProductCode
                      ? { ...item, productCode }
                      : item,
                );

              if (
                !selectedProductFromGroup.some(
                  (item) => item.parentProductCode === parentProductCode,
                )
              ) {
                selectedProductFromGroup.push({
                  parentProductCode,
                  productCode,
                });
              }

              return {
                ...state,
                performanceTileView: {
                  ...state.performanceTileView,
                  selectedProductFromGroup,
                },
              };
            },
            false,
            { type: 'setSelectedProductFromGroup' },
          ),
        /**
         * Action to handle tab changes based on the selected view.
         *
         * @param {number} tabIndex - The index of the selected tab.
         *
         * This action updates the state based on the selected view. If the selected view is a table,
         * it updates the selected tab index for the table view. If the selected view is chart tiles,
         * it maps the tab index to a corresponding fund return key and updates the performance tile view
         * with the new tab index, sort direction, and fund return key.
         *
         * @returns {void}
         */
        tabChangeHandler: (tabIndex) =>
          set(
            (state) => {
              if (state.selectedView === View.TABLE) {
                return {
                  ...state,
                  selectedTabInTableView: tabIndex,
                };
              } else if (state.selectedView === View.CHART_TILES) {
                const fundReturnKey =
                  {
                    [Constants.TILES_TAB_INDEX.ONE_YEAR]:
                      FundReturnKey.ONE_YEAR,
                    [Constants.TILES_TAB_INDEX.THREE_YEARS]:
                      FundReturnKey.THREE_YEARS,
                    [Constants.TILES_TAB_INDEX.FIVE_YEARS]:
                      FundReturnKey.FIVE_YEARS,
                    [Constants.TILES_TAB_INDEX.TEN_YEARS]:
                      FundReturnKey.TEN_YEARS,
                    [Constants.TILES_TAB_INDEX.SINCE_INCEPTION]:
                      FundReturnKey.SINCE_INCEPTION,
                  }[tabIndex] || FundReturnKey.ONE_YEAR;

                return {
                  ...state,
                  performanceTileView: {
                    ...state.performanceTileView,
                    selectedTab: tabIndex,
                    currentFundReturnKey: fundReturnKey,
                  },
                };
              }
              return state;
            },
            false,
            { type: 'tabChangeHandler' },
          ),
      };
    },
    { trace: true },
  ),
);

export const zustandStateToComponent =
  ZustandStateToComponent(productCenterStore);

export default productCenterStore;
